import React, { useState } from 'react';
import { Box, ButtonBase, FormControl, List, ListItemButton, ListItemText, Menu, MenuItem, Select, Stack, SwipeableDrawer, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { SitesResponse, SlateResponse, SportsResponse } from '../common/store/types';
import { setSite, setSlate, setSport, setType } from '../common/store/actions';
import { siteLogos, sportLogos } from '../utils/logos';
import { useGetSlateInfoQuery, useGetSlates } from '../common/queries/slateQueries';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { NumberParam, StringParam, useQueryParams } from 'use-query-params';
import { filterSlates, getSupportedSports, SupportedSites } from '../common/helpers/SelectorHelpers';
import { lineupBuilderPoolSizeOptions, percentToFirstOptions } from '../common/selectOptions';
import { setSelectedLineup, setPoolSize, setPercentChange } from './store/actions';
import SettingsSelectors from '../common/SettingsSelectors';
import { MoonLoader } from 'react-spinners';
import { getIsMobileApp } from '../../utils/mobileApp';
import { ABOVE_BOTTOM_NAV_ZINDEX } from '../common/MobileAppBottomNav';
import { IRootState } from '../../store/reducer';

type DrawerType = 'sport' | 'site' | 'slate' | 'type' | 'poolSize' | 'percentToFirst' | null;

const DRAWER_TITLES: Record<string, string> = {
  sport: 'League',
  site: 'Site',
  slate: 'Slate',
  type: 'Type',
  poolSize: 'Pool Size',
  percentToFirst: 'Percent to First',
};

const TYPE_OPTIONS = [
  { label: 'Classic', value: 'CLASSIC' },
  { label: 'Showdown', value: 'SHOWDOWN' },
];

// Compact labels for mobile; values come from the shared desktop option lists
const POOL_SIZE_LABELS: Record<number, string> = { 250: 'Small (<1000)', 1000: 'Large (>1000)' };
const POOL_SIZE_OPTIONS = lineupBuilderPoolSizeOptions.map(({ name, value }) => ({ label: POOL_SIZE_LABELS[value] ?? name, value }));

const PERCENT_OPTIONS = percentToFirstOptions.map(({ name, value }) => ({ label: name, value }));

const SelectorChip: React.FC<{ label: string; onClick: React.MouseEventHandler<HTMLElement> }> = ({ label, onClick }) => (
  <ButtonBase
    onClick={onClick}
    sx={{
      border: '1px solid rgba(255,255,255,0.3)',
      borderRadius: '20px',
      padding: '8px 12px',
      fontSize: '12px',
      color: 'rgba(255,255,255,0.87)',
      textTransform: 'uppercase',
      whiteSpace: 'nowrap',
      userSelect: 'none',
      flexShrink: 0,
    }}
  >
    {label}
  </ButtonBase>
);

const SLSSettingsSelectors: React.FC = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobileApp = getIsMobileApp();

  const { selectedSite, selectedSlate, selectedSport, selectedType } = useSelector((state: IRootState) => state.common);
  const { poolSize, percentToFirst } = useSelector((state: IRootState) => state.lineupBuilder);

  const history = useHistory();
  const [search, setSearch] = useQueryParams({ site: StringParam, slate: NumberParam });
  const { data, isLoading: slatesLoading } = useGetSlates('SINGLELINEUPSIM', false);
  const slateId = selectedSlate?.slateId?.toString() ?? '';
  const { data: updateInfo } = useGetSlateInfoQuery(slateId, !selectedSlate?.slateId);
  const slates = data?.slates;
  const sports = getSupportedSports('SINGLELINEUPSIM');
  const sites = SupportedSites;

  const [openDrawer, setOpenDrawer] = useState<DrawerType>(null);
  const [sportAnchor, setSportAnchor] = useState<HTMLElement | null>(null);
  const [siteAnchor, setSiteAnchor] = useState<HTMLElement | null>(null);
  const [slateAnchor, setSlateAnchor] = useState<HTMLElement | null>(null);
  const [typeAnchor, setTypeAnchor] = useState<HTMLElement | null>(null);

  const filteredSlates = filterSlates(slates || [], selectedSport, selectedSite, selectedType);

  const closeAll = () => {
    setOpenDrawer(null);
    setSportAnchor(null);
    setSiteAnchor(null);
    setSlateAnchor(null);
    setTypeAnchor(null);
  };

  const handleSportSelect = (sport: SportsResponse) => {
    dispatch(setSport(sport));
    dispatch(setSelectedLineup(null));
    const newSlate = filterSlates(slates || [], sport, selectedSite, selectedType)[0];
    dispatch(setSlate(newSlate || {} as SlateResponse));

    // Single push with the params inlined — pushing the path separately would
    // drop ?site/?slate and cost the user two back presses.
    const params = new URLSearchParams();
    if (search.site) params.set('site', search.site);
    if (newSlate) params.set('slate', String(newSlate.slateId));
    const query = params.toString();
    history.push(`/singlelineupsim/${sport.sport}${query ? `?${query}` : ''}`);
    closeAll();
  };

  const handleSiteSelect = (site: SitesResponse) => {
    dispatch(setSite(site));
    dispatch(setSelectedLineup(null));
    const newSlate = filterSlates(slates || [], selectedSport, site, selectedType)[0];
    if (newSlate) {
      dispatch(setSlate(newSlate));
      setSearch({ ...search, slate: Number(newSlate.slateId), site: site.site }, 'push');
    } else {
      dispatch(setSlate({} as SlateResponse));
      setSearch({ ...search, slate: undefined, site: site.site }, 'push');
    }
    closeAll();
  };

  const handleSlateSelect = (slate: SlateResponse) => {
    dispatch(setSlate(slate));
    dispatch(setSelectedLineup(null));
    setSearch({ ...search, slate: Number(slate.slateId) }, 'push');
    closeAll();
  };

  const handleTypeSelect = (type: string) => {
    dispatch(setType(type));
    dispatch(setSelectedLineup(null));
    const newSlate = filterSlates(slates || [], selectedSport, selectedSite, type)[0];
    if (newSlate) {
      dispatch(setSlate(newSlate));
      setSearch({ ...search, slate: Number(newSlate.slateId) }, 'push');
    } else {
      dispatch(setSlate({} as SlateResponse));
      setSearch({ ...search, slate: undefined }, 'push');
    }
    closeAll();
  };

  const handlePoolSizeSelect = (size: number) => {
    dispatch(setPoolSize(size));
    closeAll();
  };

  const handlePercentSelect = (percent: number) => {
    dispatch(setPercentChange(percent));
    closeAll();
  };

  if (!isMobile) {
    return <SettingsSelectors app="SINGLELINEUPSIM" />;
  }

  if (slatesLoading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <MoonLoader color="white" size={20} />
      </Box>
    );
  }

  const slateLabel = selectedSlate?.name
    ? selectedSlate.name.length > 14
      ? selectedSlate.name.substring(0, 14) + '…'
      : selectedSlate.name
    : 'Slate';
  const siteLabel = siteLogos[selectedSite?.site?.toLowerCase()]?.name || selectedSite?.site || 'Site';
  const sportLabel = selectedSport?.sport || 'League';
  const typeLabel = selectedType
    ? selectedType.charAt(0) + selectedType.slice(1).toLowerCase()
    : 'Classic';
  const openSelector = (type: DrawerType, e: React.MouseEvent<HTMLElement>) => {
    if (isMobileApp) {
      setOpenDrawer(type);
    } else {
      if (type === 'sport') setSportAnchor(e.currentTarget);
      if (type === 'site') setSiteAnchor(e.currentTarget);
      if (type === 'slate') setSlateAnchor(e.currentTarget);
      if (type === 'type') setTypeAnchor(e.currentTarget);
    }
  };

  return (
    <Box sx={{ pt: 2, pb: 1, px: 2, overflow: 'hidden', width: '100%' }}>
      {/* Row 1: League / Site / Type / Slate */}
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        sx={{ overflowX: 'auto', '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none' }}
      >
        <SelectorChip label={sportLabel} onClick={(e) => openSelector('sport', e)} />
        <Box sx={{ width: '1px', height: '16px', backgroundColor: 'rgba(255,255,255,0.1)', flexShrink: 0 }} />
        <SelectorChip label={siteLabel} onClick={(e) => openSelector('site', e)} />
        <SelectorChip label={typeLabel} onClick={(e) => openSelector('type', e)} />
        {filteredSlates.length > 0 && (
          <SelectorChip label={slateLabel} onClick={(e) => openSelector('slate', e)} />
        )}
      </Stack>
      {/* Row 2: Pool Size / Percent to First dropdowns + timestamps */}
      <Stack direction="row" gap={1.5} sx={{ mt: 2 }} alignItems="flex-end">
        <Stack gap={0.5}>
          <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>Pool Size</Typography>
          <FormControl size="small">
            <Select
              value={poolSize}
              onChange={(e) => handlePoolSizeSelect(e.target.value as number)}
              sx={{
                color: 'white',
                fontSize: '13px',
                height: 40,
                '& .MuiSelect-select': { padding: '8px 32px 8px 12px' },
                '& .MuiOutlinedInput-notchedOutline': { border: '1px solid rgba(255,255,255,0.2)' },
                '&:hover .MuiOutlinedInput-notchedOutline': { border: '1px solid rgba(255,255,255,0.3)' },
                '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.5)' },
                borderRadius: '8px',
              }}
            >
              {POOL_SIZE_OPTIONS.map(({ label, value }) => (
                <MenuItem key={value} value={value} sx={{ fontSize: 13, minHeight: 44 }}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        <Stack gap={0.5}>
          <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>% to 1st</Typography>
          <FormControl size="small">
            <Select
              value={percentToFirst}
              onChange={(e) => handlePercentSelect(e.target.value as number)}
              sx={{
                color: 'white',
                fontSize: '13px',
                height: 40,
                minWidth: 70,
                '& .MuiSelect-select': { padding: '8px 32px 8px 12px' },
                '& .MuiOutlinedInput-notchedOutline': { border: '1px solid rgba(255,255,255,0.2)' },
                '&:hover .MuiOutlinedInput-notchedOutline': { border: '1px solid rgba(255,255,255,0.3)' },
                '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.5)' },
                borderRadius: '8px',
              }}
            >
              {PERCENT_OPTIONS.map(({ label, value }) => (
                <MenuItem key={value} value={value} sx={{ fontSize: 13, minHeight: 44 }}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        {(updateInfo?.projectionsLastUpdated || updateInfo?.ownershipLastUpdated) && (
          <Stack justifyContent="flex-end" pb='4px'>
            {updateInfo?.projectionsLastUpdated && (
              <Typography sx={{ fontSize: '10px', color: 'rgba(255,255,255,0.6)', whiteSpace: 'nowrap' }}>
                Proj Updated: {moment.utc(updateInfo.projectionsLastUpdated).local().format('M/D h:mm A')}
              </Typography>
            )}
            {updateInfo?.ownershipLastUpdated && (
              <Typography sx={{ fontSize: '10px', color: 'rgba(255,255,255,0.6)', whiteSpace: 'nowrap' }}>
                Own Updated: {moment.utc(updateInfo.ownershipLastUpdated).local().format('M/D h:mm A')}
              </Typography>
            )}
          </Stack>
        )}
      </Stack>

      {/* ── Mobile web: anchor menus ── */}
      {!isMobileApp && (
        <>
          <Menu anchorEl={sportAnchor} open={Boolean(sportAnchor)} onClose={closeAll}
            PaperProps={{ sx: { backgroundColor: '#0A0A0A', color: 'white' } }}>
            {sports?.map((sport) => {
              const logo = sportLogos[sport.sport?.toUpperCase()]?.logo;
              const invertBg = sportLogos[sport.sport?.toUpperCase()]?.invertBackground;
              return (
                <MenuItem key={sport.sport} onClick={() => handleSportSelect(sport)} sx={{ fontSize: 12, gap: 1 }}>
                  {logo && (
                    <Box sx={{ width: 20, height: 20, flexShrink: 0, backgroundColor: invertBg ? 'white' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <img src={logo} alt={sport.sport} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    </Box>
                  )}
                  {sport.sport}
                </MenuItem>
              );
            })}
          </Menu>

          <Menu anchorEl={siteAnchor} open={Boolean(siteAnchor)} onClose={closeAll}
            PaperProps={{ sx: { backgroundColor: '#0A0A0A', color: 'white' } }}>
            {sites?.map((site) => (
              <MenuItem key={site.site} onClick={() => handleSiteSelect(site)} sx={{ fontSize: 12 }}>
                {siteLogos[site.site?.toLowerCase()]?.name || site.site}
              </MenuItem>
            ))}
          </Menu>

          <Menu anchorEl={typeAnchor} open={Boolean(typeAnchor)} onClose={closeAll}
            PaperProps={{ sx: { backgroundColor: '#0A0A0A', color: 'white' } }}>
            {TYPE_OPTIONS.map(({ label, value }) => (
              <MenuItem key={value} onClick={() => handleTypeSelect(value)} sx={{ fontSize: 12 }}>
                {label}
              </MenuItem>
            ))}
          </Menu>

          <Menu anchorEl={slateAnchor} open={Boolean(slateAnchor)} onClose={closeAll}
            PaperProps={{ sx: { backgroundColor: '#0A0A0A', color: 'white' } }}>
            {filteredSlates.map((slate) => (
              <MenuItem key={slate.slateId} onClick={() => handleSlateSelect(slate)} sx={{ fontSize: 12 }}>
                {`${slate.name} (${new Date(slate.startTime).toLocaleDateString([], { month: 'short', day: '2-digit', timeZone: 'America/New_York' })} · ${new Date(slate.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZone: 'America/New_York' })} ET)`}
              </MenuItem>
            ))}
          </Menu>

        </>
      )}

      {/* ── Mobile app: bottom sheet ── */}
      {isMobileApp && (
        <SwipeableDrawer
          anchor="bottom"
          open={openDrawer !== null}
          onOpen={() => {}}
          onClose={closeAll}
          disableSwipeToOpen
          sx={{ zIndex: ABOVE_BOTTOM_NAV_ZINDEX }}
          PaperProps={{
            sx: {
              backgroundColor: '#1A1A2E',
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              maxHeight: '60vh',
              pt: 1,
              pb: 2,
            },
          }}
        >
          <Box sx={{ width: '40px', height: '4px', backgroundColor: 'rgba(255, 255, 255, 0.3)', borderRadius: '8px', margin: '8px auto 16px' }} />

          {openDrawer && (
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 1, px: 2 }}>
              {DRAWER_TITLES[openDrawer]}
            </Typography>
          )}

          <List disablePadding sx={{ overflowY: 'auto' }}>
            {openDrawer === 'sport' && sports?.map((sport) => {
              const logo = sportLogos[sport.sport?.toUpperCase()]?.logo;
              const invertBg = sportLogos[sport.sport?.toUpperCase()]?.invertBackground;
              return (
                <ListItemButton key={sport.sport} onClick={() => handleSportSelect(sport)}
                  selected={selectedSport?.sport === sport.sport}
                  sx={{ gap: 1.5, px: 2, '&.Mui-selected': { backgroundColor: 'rgba(255,255,255,0.08)' } }}>
                  {logo && (
                    <Box sx={{ width: 22, height: 22, flexShrink: 0, backgroundColor: invertBg ? 'white' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <img src={logo} alt={sport.sport} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    </Box>
                  )}
                  <ListItemText primary={sport.sport} primaryTypographyProps={{ sx: { color: 'white', fontSize: 14 } }} />
                </ListItemButton>
              );
            })}

            {openDrawer === 'site' && sites?.map((site) => {
              const logo = siteLogos[site.site?.toLowerCase()]?.logo;
              return (
                <ListItemButton key={site.site} onClick={() => handleSiteSelect(site)}
                  selected={selectedSite?.site === site.site}
                  sx={{ gap: 1.5, px: 2, '&.Mui-selected': { backgroundColor: 'rgba(255,255,255,0.08)' } }}>
                  {logo && (
                    <Box sx={{ width: 22, height: 22, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <img src={logo} alt={site.site} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    </Box>
                  )}
                  <ListItemText primary={siteLogos[site.site?.toLowerCase()]?.name || site.site} primaryTypographyProps={{ sx: { color: 'white', fontSize: 14 } }} />
                </ListItemButton>
              );
            })}

            {openDrawer === 'type' && TYPE_OPTIONS.map(({ label, value }) => (
              <ListItemButton key={value} onClick={() => handleTypeSelect(value)}
                selected={selectedType === value}
                sx={{ px: 2, '&.Mui-selected': { backgroundColor: 'rgba(255,255,255,0.08)' } }}>
                <ListItemText primary={label} primaryTypographyProps={{ sx: { color: 'white', fontSize: 14 } }} />
              </ListItemButton>
            ))}

            {openDrawer === 'slate' && filteredSlates.map((slate) => (
              <ListItemButton key={slate.slateId} onClick={() => handleSlateSelect(slate)}
                selected={selectedSlate?.slateId === slate.slateId}
                sx={{ px: 2, '&.Mui-selected': { backgroundColor: 'rgba(255,255,255,0.08)' } }}>
                <ListItemText
                  primary={slate.name}
                  secondary={`${new Date(slate.startTime).toLocaleDateString([], { month: 'short', day: '2-digit', timeZone: 'America/New_York' })} · ${new Date(slate.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZone: 'America/New_York' })} ET`}
                  primaryTypographyProps={{ sx: { color: 'white', fontSize: 14 } }}
                  secondaryTypographyProps={{ sx: { color: 'rgba(255,255,255,0.5)', fontSize: 11 } }}
                />
              </ListItemButton>
            ))}

          </List>
        </SwipeableDrawer>
      )}
    </Box>
  );
};

export default SLSSettingsSelectors;
